import { Spinner } from '@chakra-ui/react'
import type React from 'react'
import DownloadIcon from '../../../../../assets/download-04-stroke-rounded'
import Icon from '../../../../../components/Icon'
import Tooltip from '../../../../../components/Tooltip'
import TransferStatusBadge from '../../../../../components/TransferStatusBadge'
import type { SwapDto } from '../../../../../types/coreApi-types'
import formatWalletAdress from '../../../../../utils/formatWalletAddress'
import { formatNumber, formatUtcDate } from '../../../../../utils/formatters'
import getStatusFromSwap from '../../../../../utils/getStatusFromSwap'

interface TableRowProps {
  swap: SwapDto
  onClick?: () => void
  disableStatusModal?: boolean
  onDownloadInvoice?: (swap: SwapDto) => void
  isDownloading?: boolean
}

const TableRow: React.FC<TableRowProps> = ({
  swap,
  onClick,
  disableStatusModal,
  onDownloadInvoice,
  isDownloading,
}) => {
  const isWalletTransfer = Boolean(swap.withdraw?.toWallet)
  const isBankTransfer = Boolean(swap.withdraw?.toBankAccount)

  const swapWithdrawNetwork = swap.withdraw?.toWallet?.network
  const bankAccountDetails = swap.withdraw?.toBankAccount?.bankAccountDetails

  const transactionStatus = getStatusFromSwap(swap)

  return (
    <div
      onClick={onClick}
      className={`w-full grid ${disableStatusModal ? 'grid-cols-3 lg:grid-cols-7' : 'grid-cols-3 lg:grid-cols-6'} hover:bg-brand-light-background dark:hover:bg-brand-dark-background border-[1px] px-6 py-3 ${
        !disableStatusModal ? 'cursor-pointer' : ''
      }`}
    >
      <p className="cell">{formatUtcDate(swap.createdAt)}</p>
      <Tooltip
        label={
          <div>
            <TransferStatusBadge
              transferStatus={transactionStatus.transferStatus}
            />

            <div className="mt-2">
              <p>{transactionStatus.title}</p>
              <p className="text-description">
                {transactionStatus.description}
              </p>
            </div>
          </div>
        }
      >
        <div className="cell">
          <TransferStatusBadge transferStatus={swap.swapStatus} />
        </div>
      </Tooltip>

      {swap.deposit && swap.withdraw && (
        <>
          <p className="cell">
            {formatNumber(swap.deposit.amount, swap.deposit.currency)}
          </p>
          <p className="cell hidden lg:flex">
            {formatNumber(swap.withdraw.amount, swap.withdraw.currency)}
          </p>
        </>
      )}

      {isWalletTransfer && (
        <Tooltip
          label={
            <div className="flex items-center gap-3">
              <Icon size="md" src={swapWithdrawNetwork} />

              <div>
                <p>
                  {swap.withdraw?.toWallet?.name ||
                    swap.withdraw?.toWallet?.address}
                </p>
                <p className="text-description">
                  {swap.withdraw?.toWallet?.network?.name}
                </p>
              </div>
            </div>
          }
        >
          <p className="cell hidden lg:flex gap-2">
            <Icon size="base" src={swapWithdrawNetwork} />

            {formatWalletAdress(
              swap.withdraw?.toWallet?.name || swap.withdraw?.toWallet?.address
            )}
          </p>
        </Tooltip>
      )}
      {isBankTransfer && (
        <Tooltip
          label={
            <div className="flex items-center gap-3">
              <div>
                <p>
                  {bankAccountDetails?.accountNumber ||
                    bankAccountDetails?.iban}
                </p>
                <p className="text-description">
                  {bankAccountDetails?.registration || bankAccountDetails?.bic}
                </p>
              </div>
            </div>
          }
        >
          <p className="cell hidden lg:flex gap-2">
            {bankAccountDetails?.accountNumber || bankAccountDetails?.iban}
          </p>
        </Tooltip>
      )}

      {/* Network/Bank Name Column - Always present */}
      {swapWithdrawNetwork && (
        <p className="cell hidden lg:flex gap-2">
          <Icon size="base" src={swapWithdrawNetwork} />
          {swapWithdrawNetwork.name}
        </p>
      )}
      {isBankTransfer && (
        <p className="cell hidden lg:flex gap-2">
          {swap.withdraw?.toBankAccount?.name}
        </p>
      )}

      {/* Download invoice — only when disableStatusModal is true (legacy swaps).
          On mobile it drops to its own full-width row beneath the data so the
          action stays reachable; on lg+ it sits in its own grid column. */}
      {disableStatusModal && (
        <div className="cell col-span-full mt-3 border-t border-solid border-brand-light-card-border dark:border-brand-dark-card-border pt-3 lg:col-span-1 lg:mt-0 lg:border-0 lg:pt-0">
          {swap.swapStatus === 'Completed' ? (
            <button
              onClick={() => onDownloadInvoice?.(swap)}
              disabled={isDownloading}
              className="flex w-full lg:w-auto items-center justify-center gap-2 px-3 py-1.5 lg:py-1 text-sm bg-gray-100 text-black border border-gray-300 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isDownloading ? (
                <Spinner size="sm" />
              ) : (
                <DownloadIcon className="w-4 h-4" />
              )}
              {isDownloading ? 'Downloading...' : 'Download'}
            </button>
          ) : (
            <Tooltip label="Invoice can only be downloaded for completed swaps">
              <button
                disabled={true}
                className="flex w-full lg:w-auto items-center justify-center gap-2 px-3 py-1.5 lg:py-1 text-sm bg-gray-100 text-gray-400 border border-gray-300 rounded cursor-not-allowed opacity-50"
              >
                <DownloadIcon className="w-4 h-4" />
                Download
              </button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  )
}

export default TableRow
